import {
    type ImageStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    SiteLogoMarkRadiusConstantType,
    SiteLogoMarkSizeConstantType,
    SiteLogoSizeKeyConstantType,
    SiteLogoStretchConstantType,
    SiteLogoHitAreaConstantType,
    SiteLogoColorsConstantType,
    SiteLogoSizeConstantType,
    SiteLogoDynamicStyleType,
    SiteLogoMeasuresKeys,
    SiteLogoMarkMeasures,
    SiteLogoSizeType,
    SiteLogoMeasures,
    SiteLogoColors,
    SiteLogoSize
} from "./type";
import type {
    HitAreaMeasures,
    Mutable
} from "../../types";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    webStyle
} from "../../utils";

export const SITE_LOGO_SIZE_ALIASES: Record<SiteLogoSizeType, SiteLogoSize> = {
    xxxLarge: "large",
    medium: "medium",
    xSmall: "medium",
    xxLarge: "large",
    xxSmall: "small",
    xLarge: "large",
    small: "small",
    large: "large"
};

export const SITE_LOGO_SIZES: Record<SiteLogoSize, SiteLogoMeasuresKeys> = {
    large: {
        subTitleSize: "bodyMediumSize",
        titleSize: "titleLargeSize",
        iconSize: "large",
        gap: "spacingMd",
        size: "large"
    },
    medium: {
        subTitleSize: "bodySmallSize",
        titleSize: "titleMediumSize",
        iconSize: "medium",
        gap: "spacingSm",
        size: "medium"
    },
    small: {
        subTitleSize: "bodySmallSize",
        titleSize: "labelMediumSize",
        iconSize: "small",
        gap: "spacingSm",
        size: "small"
    }
};

export const getSiteLogoSizeKey = ({
    size
}: SiteLogoSizeKeyConstantType): SiteLogoSize => {
    return SITE_LOGO_SIZE_ALIASES[size];
};

export const getSiteLogoIsStretched = ({
    surfaceStyle
}: SiteLogoStretchConstantType): boolean => {
    return surfaceStyle.alignSelf === "stretch" || (typeof surfaceStyle.width === "string" && surfaceStyle.width.endsWith("%"));
};

export const getSiteLogoSize = ({
    controlHeights,
    iconSizes,
    spaces,
    size
}: SiteLogoSizeConstantType): SiteLogoMeasures => {
    const currentSize = SITE_LOGO_SIZES[getSiteLogoSizeKey({
        size
    })];

    return {
        iconSize: iconSizes[currentSize.iconSize],
        size: controlHeights[currentSize.size],
        subTitleSize: currentSize.subTitleSize,
        titleSize: currentSize.titleSize,
        gap: spaces[currentSize.gap]
    };
};

export const getSiteLogoMarkRadius = ({
    isActionable,
    radiuses,
    spaces
}: SiteLogoMarkRadiusConstantType): number => {
    if(!isActionable) {
        return radiuses.actions;
    }

    return concentric({
        inset: spaces.spacingXs,
        outer: radiuses.actions,
        role: radiuses.actions
    });
};

export const getSiteLogoMarkSize = ({
    isCustomImageSize,
    isActionable,
    currentSize,
    imageSpace,
    radiuses,
    spaces
}: SiteLogoMarkSizeConstantType): SiteLogoMarkMeasures => {
    const radius = getSiteLogoMarkRadius({
        isActionable,
        radiuses,
        spaces
    });

    const imageInset = imageSpace ? spaces[imageSpace] : 0;

    const width = isCustomImageSize ? currentSize.size * 3 : currentSize.size;

    return {
        imageHeight: currentSize.size - imageInset,
        imageWidth: width - imageInset,
        imageRadius: concentric({
            inset: imageInset / 2,
            outer: radius,
            role: radius
        }),
        height: currentSize.size,
        radius,
        width
    };
};

export const getSiteLogoColors = ({
    logoBackgroundColor,
    subTitleCustomColor,
    titleCustomColor,
    backgroundColor,
    borderColor,
    isDisabled,
    iconColor,
    colors
}: SiteLogoColorsConstantType): SiteLogoColors => {
    if(isDisabled) {
        return {
            markBorderColor: colors.system.state.border.disabled.neutral,
            subTitleColor: colors.system.state.content.disabled.neutral,
            titleColor: colors.system.state.content.disabled.neutral,
            markColor: colors.system.state.overlay.disabled.neutral,
            iconColor: colors.system.state.content.disabled.neutral
        };
    }

    const currentColors: SiteLogoColors = {
        subTitleColor: subTitleCustomColor ? colors.project[subTitleCustomColor] : undefined,
        iconColor: iconColor ? colors.project[iconColor] : colors.content.icon.default,
        titleColor: titleCustomColor ? colors.project[titleCustomColor] : undefined,
        markBorderColor: colors.content.container[logoBackgroundColor],
        markColor: colors.content.container.subtle
    };

    if(borderColor) {
        currentColors.markBorderColor = borderColor === "transparent" ? borderColor : colors.content.border[borderColor];
    }

    if(backgroundColor) {
        currentColors.markColor = backgroundColor === "transparent" ? backgroundColor : colors.content.container[backgroundColor];
    }

    return currentColors;
};

export const getSiteLogoHitArea = ({
    currentMarkSize,
    touchTargets,
    pointerType,
    spaces
}: SiteLogoHitAreaConstantType): HitAreaMeasures => {
    return getHitArea({
        visualSize: currentMarkSize.height + spaces.spacingXs * 2,
        touchTargets,
        pointerType
    });
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "flex-start",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    mark: {
        justifyContent: "center",
        borderStyle: "solid",
        alignItems: "center",
        flexShrink: 0
    },
    titles: {
        justifyContent: "center",
        flexShrink: 1,
        minWidth: 0
    },
    root: {
        justifyContent: "center",
        alignItems: "flex-start"
    },
    content: {
        flexDirection: "row",
        alignItems: "center",
        maxWidth: "100%"
    },
    surface: {
        position: "relative",
        maxWidth: "100%"
    },
    image: {
        borderStyle: "solid",
        objectFit: "cover"
    },
    flexRootContainer: {
        maxWidth: "100%"
    }
});

export const useStyles = ({
    currentMarkSize,
    isWorkWithFlex1,
    isFocusVisible,
    isTitleVisible,
    currentColors,
    isActionable,
    isShowBorder,
    shapeConfigs,
    touchTargets,
    currentSize,
    pointerType,
    isFlexRoot,
    isLoading,
    radiuses,
    borders,
    colors,
    spaces
}: SiteLogoDynamicStyleType) => {
    const styles = {
        surface: {
            paddingEnd: isTitleVisible ? spaces.spacingSm : spaces.spacingXs,
            paddingVertical: spaces.spacingXs,
            paddingStart: spaces.spacingXs,
            ...getCornerStyle({
                radius: radiuses.actions,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        image: {
            borderColor: currentColors.markBorderColor,
            ...getCornerStyle({
                radius: currentMarkSize.imageRadius,
                shapeConfigs
            }),
            height: currentMarkSize.imageHeight,
            width: currentMarkSize.imageWidth
        } as Mutable<ImageStyle>,
        mark: {
            borderColor: currentColors.markBorderColor,
            backgroundColor: currentColors.markColor,
            ...getCornerStyle({
                radius: currentMarkSize.radius,
                shapeConfigs
            }),
            height: currentMarkSize.height,
            width: currentMarkSize.width
        } as Mutable<ViewStyle>,
        content: {
            gap: currentSize.gap
        } as Mutable<ViewStyle>,
        container: {
        } as Mutable<ViewStyle>,
        root: {
        } as Mutable<ViewStyle>
    };

    if(isShowBorder) {
        styles.mark.borderWidth = borders.subtract;
        styles.image.borderWidth = borders.subtract;
    }

    if(isActionable) {
        const hitArea = getSiteLogoHitArea({
            currentMarkSize,
            touchTargets,
            pointerType,
            spaces
        });

        styles.container.minHeight = hitArea.minHeight;
        styles.container.minWidth = hitArea.minWidth;

        Object.assign(styles.surface, getFocusRingStyle({
            colorKey: "default",
            isFocusVisible,
            borders,
            colors
        }));
    }

    if(isActionable && isLoading) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    if(isFlexRoot) {
        Object.assign(styles.container, stylesheet.flexRootContainer);

        styles.root.flex = 1;
    }

    if(isWorkWithFlex1 && !isFlexRoot) {
        styles.container.flex = 1;
    }

    return styles;
};
export default stylesheet;
